import { Button, Typography, Divider } from 'antd';
import {
	UserAddOutlined,
	InfoCircleOutlined,
	DollarOutlined,
	SettingOutlined,
	DeleteOutlined,
} from '@ant-design/icons';
import { PhoneOutlined, SoundOutlined } from '@ant-design/icons';
import { CustomModal } from '@/components/CustomModal/CustomModal';
import { useMutation } from '@tanstack/react-query';
import { purchaseInboundSeat, purchaseOutboundSeat } from '@/api/user';
import { notification } from 'antd';

const { Text } = Typography;

interface AddAgentSubscriptionModalProps {
	visible: boolean;
	onCancel: () => void;
	onContinue: () => void;
	agentType: 'inbound' | 'outbound';
}

const AddAgentSubscriptionModal: React.FC<AddAgentSubscriptionModalProps> = ({
	visible,
	onCancel,
	onContinue,
	agentType,
}) => {
	const isInbound = agentType === 'inbound';
	const agentIcon = isInbound ? <PhoneOutlined /> : <SoundOutlined />;
	const agentTypeName = isInbound ? 'Inbound' : 'Outbound';

	const purchaseInboundSeatMutation = useMutation({
		mutationKey: ['purchaseInboundSeat'],
		mutationFn: purchaseInboundSeat,
	});

	const purchaseOutboundSeatMutation = useMutation({
		mutationKey: ['purchaseOutboundSeat'],
		mutationFn: purchaseOutboundSeat,
	});

	const handleContinue = async () => {
		try {
			if (isInbound) {
				await purchaseInboundSeatMutation.mutateAsync(1);
			} else {
				await purchaseOutboundSeatMutation.mutateAsync(1);
			}

			notification.success({
				message: 'Agent Added Successfully',
				description: `Your new ${agentTypeName.toLowerCase()} agent has been added to your subscription.`,
				placement: 'topRight',
			});

			onContinue();
		} catch {
			notification.error({
				message: 'Failed to Add Agent',
				description: 'There was an error adding the agent to your subscription. Please try again.',
				placement: 'topRight',
			});
		}
	};

	return (
		<CustomModal
			isModalOpen={visible}
			cancelAction={onCancel}
			confirmAction={handleContinue}
			title={`Add New ${agentTypeName} Agent`}
			description="Configure your new agent and manage it anytime"
			icon={<UserAddOutlined />}
			footer={null}
			width={700}
			className="add-agent-modal"
		>
			<div className="p-6">
				<div className="space-y-4 mb-6">
					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<InfoCircleOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								What you'll get
							</Text>
							<Text type="secondary" className="block">
								A new {agentTypeName.toLowerCase()} agent that you can configure and manage
								according to your needs. You can modify its settings or remove it at any time.
							</Text>
						</div>
					</div>

					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<DollarOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								Billing Details
							</Text>
							<Text type="secondary" className="block">
								You'll be charged a prorated amount immediately for the remaining days in your
								current billing cycle. Starting from your next billing cycle, the full subscription
								amount will be charged if you continue to keep the agent.
							</Text>
						</div>
					</div>

					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<SettingOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								Configuration
							</Text>
							<Text type="secondary" className="block">
								After adding the agent, you can configure its settings, customize its behavior, and
								manage it according to your requirements.
							</Text>
						</div>
					</div>

					<div className="flex items-start gap-3">
						<div className="bg-gray-100 p-2 rounded-lg mt-1">
							<DeleteOutlined className="text-lg text-gray-600" />
						</div>
						<div>
							<Text strong className="block mb-1">
								Flexible Management
							</Text>
							<Text type="secondary" className="block">
								You have complete control over your subscription. You can remove this agent from
								your subscription at any time, and you'll only be charged for the days you used it.
							</Text>
						</div>
					</div>
				</div>

				<Divider className="my-4" />

				<div className="flex justify-end gap-3">
					<Button onClick={onCancel}>Cancel</Button>
					<Button
						type="primary"
						onClick={handleContinue}
						icon={agentIcon}
						loading={
							purchaseInboundSeatMutation.isPending || purchaseOutboundSeatMutation.isPending
						}
					>
						Add {agentTypeName} Agent
					</Button>
				</div>
			</div>
		</CustomModal>
	);
};

export default AddAgentSubscriptionModal;
